import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Divider,
  Spinner,
  Badge,
  IconButton,
} from '@chakra-ui/react';
import { FaRegBell, FaInfoCircle } from 'react-icons/fa';
import { fetchNotifications, markNotificationAsRead } from '../../../API/notification';

const NotificationsMenu = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 5 * 60 * 1000;

  const loadNotifications = useCallback(async (force = false) => {
    try {
      const now = Date.now();
      if (!force && now - lastFetchTimeRef.current < CACHE_DURATION && unreadCount === 0) {
        return;
      }
      lastFetchTimeRef.current = now;
      setLoading(true);
      setError(null);

      const user = localStorage.getItem('user');
      if (!user) {
        setError('User data not found');
        return;
      }

      const { userId } = JSON.parse(user);
      const data = await fetchNotifications(userId);
      if (data.success) {
        const unreadNotifications = data.notifications.filter(notification => !notification.read);
        setNotifications(unreadNotifications);
        setUnreadCount(unreadNotifications.length);
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Error handling notifications');
    } finally {
      setLoading(false);
    }
  }, [CACHE_DURATION, unreadCount]);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      if (notifications.length === 0) return;
      const unreadNotifications = notifications.filter(notification => !notification.read);
      if (unreadNotifications.length === 0) return;

      await Promise.all(
        unreadNotifications.map(notification => markNotificationAsRead(notification.notificationId))
      );
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  }, [notifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadNotifications();
    }, 15 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [loadNotifications]);

  return (
    <Menu
      isOpen={isOpen}
      onOpen={() => {
        setIsOpen(true);
        loadNotifications(true);
      }}
      onClose={() => {
        setIsOpen(false);
        markAllNotificationsAsRead();
      }}
    >
      <MenuButton
        as={IconButton}
        icon={
          <Box position="relative">
            <FaRegBell color={unreadCount > 0 ? 'red' : 'black'} />
            {unreadCount > 0 && (
              <Badge
                colorScheme="red"
                variant="solid"
                borderRadius="full"
                position="absolute"
                top="-1"
                right="-1"
                fontSize="xs"
                minWidth="16px"
                textAlign="center"
              >
                {unreadCount}
              </Badge>
            )}
          </Box>
        }
        aria-label="Notifications"
        style={{ backgroundColor: "#A4B5C" }}
        fontSize="xl"
        bgColor="#FAFAF7"
      />

      <MenuList className="menu-list" maxH="300px" overflowY="auto" bg="white">
        {loading ? (
          <MenuItem bg="white" _hover={{ bg: 'white' }}>
            <Spinner size="lg" />
            <Box ml={2}>Loading...</Box>
          </MenuItem>
        ) : error ? (
          <MenuItem bg="white" _hover={{ bg: 'white' }}>{error}</MenuItem>
        ) : notifications.length > 0 ? (
          notifications.map(notification => (
            <MenuItem key={notification.notificationId} bg="white" _hover={{ bg: 'white' }}>
              <Box display="flex" alignItems="center" p={2}>
                <FaInfoCircle style={{ marginRight: '15px', color: 'green' }} size={20} />
                <Box>
                  <strong>{notification.title}</strong>
                  <p>{notification.description}</p>
                  <Divider />
                </Box>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem bg="white" _hover={{ bg: 'white' }}>No notifications available</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

export default NotificationsMenu;
